import invariant from 'invariant';
import * as React from 'react';
import BoxBody from 'components/BoxBody';
import Input from 'components/Input';
import Link from 'components/Link';
import FormElement from 'components/FormElement';
import BoxHeader from 'components/BoxHeader';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import DeviceActions from 'actions/DeviceActions';
import Button from 'components/Button';
import BoxFooter from 'components/BoxFooter';
import assertions from 'lib/assertions';
// @ts-ignore
import { stripSpaces } from 'lib/phoneNumbers';
import { pickBy, identity } from 'lodash';
import { InputChangeHandler } from 'types/typescript/eventHandlers';

// @ts-ignore
import { ConfirmableDeviceType, deviceString } from 'entities/Device';

import './styles/LinkDeviceVerificationCodeBox.scss';

export type SendType = 'text' | 'voice';

type DefaultProps = {
  sendType: SendType;
  showFooter: boolean;
};

type Props = {
  onResend?: (newSendType?: SendType) => void;
  // This will replace calling the standard /v2/devices/outbound_verification call
  // Only used in the ForgotPasswordPage
  onSubmit?: (code: string) => void;
  // This gets called after using the normal device verification call if onSubmit isn't provided
  onDone?: (authToken?: string, deviceUid?: string) => void;
  // this is stored and formatted like the user entered it
  submittedAddress: string;
  footer?: React.ReactNode;
  showFooter?: boolean;
  successMessage?: string;
  /* Skip updating device preferences:
   *
   * When adding a new device the backend
   * try to make smart choice about enabling the added device
   * and disabeling preixisting one. This skips it, the added
   * device will be disabled, and current devices will not change.
   */
  skipUpdateDevicePreferences?: boolean;
  deviceType: ConfirmableDeviceType;
  clickEventKey?: string;
} & DefaultProps;

type State = {
  submitDisabled: boolean;
  submitCode: string;
};

class LinkDeviceVerificationCodeBox extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    sendType: 'text',
    showFooter: true,
  };

  state: State = {
    submitDisabled: true,
    submitCode: '',
  };

  handleValidation: InputChangeHandler = e => {
    assertions.lengthEqualTo(stripSpaces(e.target.value), 4);
  };

  handleValidChange: InputChangeHandler = e => {
    this.setState({ submitDisabled: false, submitCode: stripSpaces(e.target.value) });
  };

  handleInvalidChange: InputChangeHandler = () => {
    this.setState({ submitDisabled: true, submitCode: '' });
  };

  onResend = (newSendType?: SendType) => {
    this.setState({ submitDisabled: true, submitCode: '' });
    if (this.props.onResend) {
      this.props.onResend(newSendType);
    }
  };

  submit = () => {
    if (this.props.onSubmit) {
      this.props.onSubmit(this.state.submitCode);
    } else {
      const onSuccess = (device: any) => {
        if (this.props.onDone) {
          this.props.onDone(device.token, device.uid);
        }

        const readableDevice = this.props.deviceType === 'email' ? 'Email address' : 'Phone number';
        ToastActions.showSuccessToast(
          this.props.successMessage || `${readableDevice} linked to Remind`
        );
      };

      const onError = (response: any) => {
        const errorText =
          response && response.code === 'already_taken'
            ? 'This device is already taken, please contact support to merge the device.'
            : 'Please enter a valid code.';

        ToastActions.showErrorToast(errorText);
      };

      const options = pickBy(
        {
          skipUpdateDevicePreferences: this.props.skipUpdateDevicePreferences,
        },
        identity
      );

      invariant(
        this.props.submittedAddress,
        'LinkDeviceVerificationCodeBox submit without address'
      );

      DeviceActions.createOutboundDevice(
        this.props.submittedAddress,
        this.state.submitCode,
        options
      ).then(onSuccess, onError);
    }
  };

  render() {
    const { submittedAddress, deviceType, sendType } = this.props;
    const oppositeSendType = sendType === 'text' ? 'voice' : 'text';
    const subtext = (
      <React.Fragment>
        Please enter the 4-digit code sent to
        <br />
        {deviceString(submittedAddress, deviceType)}.
      </React.Fragment>
    );

    return (
      <div className="LinkDeviceVerificationCodeBox">
        <BoxHeader title="Enter your code" subtext={subtext} />
        <BoxBody divided>
          <div className="LinkDeviceVerificationCodeBox-body">
            <FormElement
              label="Verification code"
              labelHidden
              validate={this.handleValidation}
              onValidChange={this.handleValidChange}
              onInvalidChange={this.handleInvalidChange}
              element={Input}
              elementProps={{
                className: 'LinkDeviceVerificationCodeBox-body-Input',
                name: 'code',
                size: 'large',
                type: 'text',
                placeholder: '0000',
              }}
            />
            <Button
              className="LinkDeviceVerificationCodeBox-body-Submit"
              eventElementName="LinkDeviceVerificationCodeBox-body-Submit"
              eventKey={this.props.clickEventKey}
              onClick={this.submit}
              type="submit"
              disabled={this.state.submitDisabled}>
              Submit
            </Button>
          </div>
        </BoxBody>
        {this.props.showFooter && (
          <BoxFooter className="LinkDeviceVerificationCodeBox-footer">
            <div className="LinkDeviceVerificationCodeBox-footer-section">
              Didn’t receive a code?{' '}
              <Link data-test="resend-link" onClick={() => this.onResend()}>
                Resend
              </Link>
              {this.props.footer ? ' ' : null}
              {this.props.footer}
            </div>
            {deviceType === 'phoneNumber' ? (
              <div className="LinkDeviceVerificationCodeBox-footer-section">
                <Link
                  data-test="sendtype-change-link"
                  onClick={() => this.onResend(oppositeSendType)}>
                  {sendType === 'text' ? 'Call me instead' : 'Send me a text instead'}
                </Link>
              </div>
            ) : null}
          </BoxFooter>
        )}
      </div>
    );
  }
}

export default LinkDeviceVerificationCodeBox;
